import React, { createContext, useContext, useState, useEffect } from 'react';
import { mockUser, mockFarms } from '../utils/mockData';
import { STORAGE_KEYS, readStorageValue, writeStorageValue } from '../utils/storageKeys';

const LayoutContext = createContext();

export function LayoutProvider({ children }) {
  // Sidebar collapsed state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = readStorageValue(
      typeof window !== 'undefined' ? window.localStorage : null,
      STORAGE_KEYS.sidebarCollapsed
    );
    return saved?.value === 'true';
  });

  // Selected farm
  const [selectedFarm, setSelectedFarm] = useState(() => {
    const savedId = readStorageValue(
      typeof window !== 'undefined' ? window.localStorage : null,
      STORAGE_KEYS.selectedFarm
    )?.value;
    return mockFarms.find(f => f.id === savedId) || mockFarms[0];
  });

  // Current user with role
  const [currentUser, setCurrentUser] = useState(() => {
    const savedRole = readStorageValue(
      typeof window !== 'undefined' ? window.localStorage : null,
      STORAGE_KEYS.userRole
    )?.value;
    return {
      ...mockUser,
      role: savedRole || mockUser.role
    };
  });

  // Mobile menu open
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Persist sidebar collapsed state
  useEffect(() => {
    writeStorageValue(window.localStorage, STORAGE_KEYS.sidebarCollapsed, String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Persist selected farm
  useEffect(() => {
    writeStorageValue(window.localStorage, STORAGE_KEYS.selectedFarm, selectedFarm.id);
  }, [selectedFarm]);

  // Persist user role
  useEffect(() => {
    writeStorageValue(window.localStorage, STORAGE_KEYS.userRole, currentUser.role);
  }, [currentUser.role]);

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => !prev);
    
    // Dispatch event for map to resize after transition
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('sidebar:toggled'));
    }, 350); // 300ms sidebar animation + 50ms buffer
  };

  const selectFarm = (farm) => {
    setSelectedFarm(farm);
  };

  const switchRole = (newRole) => {
    setCurrentUser(prev => ({
      ...prev,
      role: newRole
    }));
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(prev => !prev);
  };

  const value = {
    sidebarCollapsed,
    setSidebarCollapsed,
    toggleSidebar,
    selectedFarm,
    selectFarm,
    currentUser,
    switchRole,
    mobileMenuOpen,
    setMobileMenuOpen,
    toggleMobileMenu,
    farms: mockFarms
  };

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
}

export function useLayout() {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within LayoutProvider');
  }
  return context;
}
